import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { clientService } from '../../../services/clientService';

const ClientSelector = ({ value, onChange, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await clientService?.getAll();
      setClients(data || []);
    } catch (err) {
      setLoadError(err?.message || 'Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients?.find(c => c?.id === value);
  const filteredClients = clients?.filter(client =>
    client?.nombre?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border transition-colors focus:outline-none
          ${error ? 'border-[#E05252]/50' : 'border-white/10 hover:border-white/20 focus:border-[#D6EF4E]/40'}
          ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          bg-[#111111]`}
      >
        {loading ? (
          <span className="text-sm font-oranienbaum text-white/30">Cargando clientes...</span>
        ) : selectedClient ? (
          <div className="flex items-center gap-2.5 flex-1 text-left min-w-0">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(214,239,78,0.1)' }}>
              <Icon name="Store" size={14} style={{ color: '#D6EF4E' }} />
            </div>
            <span className="text-sm font-oranienbaum text-white/85 truncate">{selectedClient?.nombre}</span>
          </div>
        ) : (
          <span className="text-sm font-oranienbaum text-white/30">Seleccionar cliente...</span>
        )}
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0, marginLeft: 8 }} />
      </button>

      {error && <p className="text-[11px] text-[#E05252] mt-1 font-oranienbaum">{error}</p>}
      {loadError && <p className="text-[11px] text-[#E05252] mt-1 font-oranienbaum">{loadError}</p>}

      {/* Selected client info */}
      {selectedClient && (
        <div className="mt-2 p-3 bg-[#111111] rounded-lg border border-white/8">
          <div className="grid grid-cols-1 gap-1.5 text-xs">
            <div>
              <p className="font-oranienbaum text-white/35">Nombre</p>
              <p className="font-oranienbaum font-semibold text-white/85">{selectedClient?.nombre}</p>
            </div>
            {selectedClient?.telefono && (
              <div>
                <p className="font-oranienbaum text-white/35">Teléfono</p>
                <p className="font-oranienbaum text-white/70">{selectedClient?.telefono}</p>
              </div>
            )}
            {selectedClient?.email && (
              <div>
                <p className="font-oranienbaum text-white/35">Email</p>
                <p className="font-oranienbaum text-white/70">{selectedClient?.email}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1.5 bg-[#1A1A1A] rounded-xl shadow-2xl border border-white/10 overflow-hidden">
          {/* Search */}
          <div className="p-2.5 border-b border-white/8">
            <div className="relative">
              <Icon name="Search" size={13} style={{ color: 'rgba(255,255,255,0.35)', position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full bg-[#111111] border border-white/10 text-white/85 text-xs rounded-lg pl-8 pr-3 py-2 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center">
                <div className="w-5 h-5 border-2 border-[#D6EF4E]/30 border-t-[#D6EF4E] rounded-full animate-spin mx-auto mb-2" />
                <p className="text-xs font-oranienbaum text-white/35">Cargando...</p>
              </div>
            ) : loadError ? (
              <div className="p-6 text-center">
                <Icon name="AlertCircle" size={24} style={{ color: '#E05252', margin: '0 auto 8px' }} />
                <p className="text-xs font-oranienbaum text-[#E05252]">{loadError}</p>
              </div>
            ) : filteredClients?.length > 0 ? (
              filteredClients?.map((client) => (
                <button
                  key={client?.id}
                  type="button"
                  onClick={() => {
                    onChange(client?.id);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 transition-colors text-left
                    ${value === client?.id ? 'bg-[#D6EF4E]/8' : 'hover:bg-white/5'}`}
                >
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(214,239,78,0.08)' }}>
                    <Icon name="Store" size={13} style={{ color: '#D6EF4E' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-oranienbaum text-white/85 truncate">{client?.nombre}</p>
                    {client?.email && (
                      <p className="text-[10px] font-oranienbaum text-white/35 truncate">{client?.email}</p>
                    )}
                  </div>
                  {value === client?.id && (
                    <Icon name="Check" size={13} style={{ color: '#D6EF4E', flexShrink: 0 }} />
                  )}
                </button>
              ))
            ) : (
              <div className="p-6 text-center">
                <Icon name="Search" size={24} style={{ color: 'rgba(255,255,255,0.2)', margin: '0 auto 8px' }} />
                <p className="text-xs font-oranienbaum text-white/35">No se encontraron clientes</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSelector;